import { FileContainer, process } from "am-tax-fe-core";

import { components } from "../generated/generated-api";
import { cachedApiUrl, client } from "../entities/entityApi";
import { uploadFile } from "../uploadUtils";

//#region Dto definition
export type Client = components["schemas"]["ClientDto"];
export type DeleteIndustryAnalysisDiagramDto = components["schemas"]["DeleteFileDto"];
export type FileUploadDto = components["schemas"]["FileUploadDto"];
export type IndustryAnalysisFiles = components["schemas"]["ClientIndustryDiagramsDto"];
export type CreateClient = components["schemas"]["CreateOrUpdateClientDto"];
export type UpdateClient = components["schemas"]["CreateOrUpdateClientDto"] & { id: string };

// Temporary type definition until the API types are regenerated
export type UnlinkedClient = {
    clientId: string;
    clientName: string;
    accessLevel: string;
    roleName: string;
    roleUrn: string;
};

export const getClientAsync = async (id: string): Promise<Client> =>
    process(
        client().GET("/api/clients/{clientId}", {
            params: {
                path: {
                    clientId: id,
                },
            },
        }),
    );

export const getclientsAsync = async (): Promise<Client[]> => process(client().GET("/api/clients"));

export const getUnlinkedClientsAsync = async (): Promise<UnlinkedClient[]> => {
    // Since this endpoint isn't in the generated types yet, we'll use direct fetch
    // but leverage the client's base URL by using the same import pattern as jurisdictions
    // This follows the same pattern as getJurisdictionsAsync but for an endpoint not yet in generated types

    // Use the configured client but bypass TypeScript validation for the endpoint
    const apiClient = client();
    return process(apiClient.GET("/api/clients/unlinked", {}));
};

export const createClientAsync = async (args: CreateClient): Promise<Client> => process(client().POST("/api/clients", { body: args }));

export const updateClientAsync = async (args: UpdateClient): Promise<Client> =>
    process(
        client().PUT("/api/clients/{clientId}", {
            params: {
                path: {
                    clientId: args.id,
                },
            },
            body: args,
        }),
    );

export const deleteClientAsync = async (id: string): Promise<Client> =>
    process(
        client().DELETE("/api/clients/{clientId}", {
            params: {
                path: {
                    clientId: id,
                },
            },
        }),
    );

export const getClientIndustryAnalysisFilesAsync = async (clientId: string): Promise<IndustryAnalysisFiles> =>
    process(
        client().GET("/api/clients/{clientId}/industry-analysis", {
            params: {
                path: {
                    clientId,
                },
            },
        }),
    );

export const deleteIndustryAnalysisDiagramAsync = async (clientId: string, args: DeleteIndustryAnalysisDiagramDto): Promise<string> =>
    process(
        client().DELETE("/api/clients/{clientId}/industry-analysis", {
            params: {
                path: {
                    clientId,
                },
            },
            body: args,
        }),
    );

export const uploadOrgChartFileAsync = async (file: FileContainer, clientId: string) => {
    return uploadFile(`${cachedApiUrl}/api/clients/${clientId}/orgcharts`, file);
};

export const uploadIndustryAnalysisFileAsync = async (file: FileContainer, clientId: string) => {
    return uploadFile(`${cachedApiUrl}/api/clients/${clientId}/industry-analysis`, file);
};

export const downloadClientOrgDiagramAsync = async (clientId: string): Promise<Response> => {
    const result = await client().GET("/api/clients/{clientId}/orgcharts", {
        params: {
            path: {
                clientId: clientId,
            },
        },
        parseAs: "stream",
    });

    if (!result.response || !result.response.ok) {
        throw new Error(`Failed to download org. chart: ${result.response?.statusText || "Unknown error"}`);
    }

    return result.response;
};
